import { FC, useEffect, useMemo, useState } from 'react'

import { useStatistic, useStatisticActions } from '@store'

import styles from './CollectedStarsStat.module.scss'

const getDateRange = (days: number): Date[] => {
  const result: Date[] = []
  for (let i = 0; i < days; i++) {
    const d = new Date()
    d.setDate(d.getDate() - i)
    result.push(d)
  }

  return result
}

type NormalizedStat = {
  date: string
  stars: number
}
export const CollectedStarsStat: FC = () => {
  const [currentDayIndex, setCurrentDayIndex] = useState(0)
  const { fetchCollectedStarsStatistic } = useStatisticActions()
  const starsStatistic = useStatistic((state) => state.collectedStarsStatistic)

  useEffect(() => {
    fetchCollectedStarsStatistic()
  }, [fetchCollectedStarsStatistic])

  const normalizedValues: NormalizedStat[] = useMemo(() => {
    if (!starsStatistic?.length) return []

    const DAYS_COUNT = 60
    const allDays = getDateRange(DAYS_COUNT)

    const byDate = new Map(
      starsStatistic.map((item) => [
        new Date(item.collectionDate).toDateString(),
        item.starsCount,
      ])
    )

    return allDays.map((d) => {
      const stars = byDate.get(d.toDateString()) || 0
      const day = String(d.getDate()).padStart(2, '0')
      const month = String(d.getMonth() + 1).padStart(2, '0')
      return { date: `${day}.${month}`, stars }
    })
  }, [starsStatistic])

  const daysOffsetPercent =
    normalizedValues.length > 0
      ? (currentDayIndex / normalizedValues.length) * 100
      : 0

  if (!normalizedValues.length) return null

  return (
    <div className={styles.borderGradient}>
      <div className={styles.bg}>
        <div className={styles.container}>
          <div
            className={styles.light}
            style={{
              transform: `translateX(${daysOffsetPercent - 50}%)`,
            }}
          />

          {/* SVG Chart */}
          <svg width="100%" height="100%" className={styles.chart}>
            <defs>
              <linearGradient
                id="chartGradient"
                x1="100%"
                y1="0%"
                x2="0%"
                y2="0%"
              >
                <stop offset="0%" stopColor="#181818" />
                <stop offset="50%" stopColor="#000" />
                <stop offset="100%" stopColor="#181818" />
              </linearGradient>

              <mask id="barsMask">
                <rect x="0" y="0" width="100%" height="100%" fill="white" />
                {normalizedValues.map((value, i) => {
                  const barWidth = 0.5
                  const gap =
                    (100 - normalizedValues.length * barWidth) /
                    (normalizedValues.length - 1)
                  const x = i * (barWidth + gap)

                  const isActive = currentDayIndex === i

                  return (
                    <rect
                      key={value.date}
                      x={`${isActive ? x - gap / 4 : x}%`}
                      y={`${94 - (isActive ? 85 : 5 + value.stars * 0.6)}%`}
                      width={`${isActive ? barWidth * 2 : barWidth}%`}
                      height={`${isActive ? 85 : 5 + value.stars * 0.6}%`}
                      fill="black"
                    />
                  )
                })}
              </mask>
            </defs>

            <rect
              width="100%"
              height="100%"
              fill="url(#chartGradient)"
              mask="url(#barsMask)"
            />
          </svg>

          {/* Value Display */}
          <div className={styles.value}>
            <div className={styles.stars}>
              {normalizedValues[currentDayIndex].stars}
            </div>
            <div className={styles.date}>
              <svg
                className={styles.starIcon}
                width="22"
                height="22"
                viewBox="0 0 22 22"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.4 8.6
                    C13.4 8.6 11.0 6.4 11.0 1.4
                    C11.0 6.4  8.6 8.6  8.6 8.6
                    C8.6 8.6  6.4 11.0 1.4 11.0
                    C6.4 11.0 8.6 13.4 8.6 13.4
                    C8.6 13.4 11.0 15.6 11.0 20.6
                    C11.0 15.6 13.4 13.4 13.4 13.4
                    C13.4 13.4 15.6 11.0 20.6 11.0
                    C15.6 11.0 13.4 8.6  13.4 8.6
                    Z"
                  fill="white"
                />
              </svg>
              <div className={styles.dateText}>
                /{normalizedValues[currentDayIndex].date}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Range control */}
      <div className={styles.range}>
        <input
          type="range"
          className={styles.inputRange}
          min={0}
          max={normalizedValues.length - 1}
          value={currentDayIndex}
          step={1}
          onChange={(e) => setCurrentDayIndex(Number(e.target.value))}
        />
      </div>
    </div>
  )
}
